import { canv } from "@/canv/global";
import type { TextLine } from "@/elements/text/typesV2";
import { clearTextWidthCache } from "@/elements/text/utilsV2/measurements";
import { getFontAsset, invalidateFailedFontAsset } from "@/fonts/assetStore";
import { hasFontVariant } from "@/fonts/catalog";
import type {
    FontLoadRequirements,
    FontSourceFile,
    FontStyle,
    FontVariantToLoad,
} from "@/fonts/types";
import { FONT_REGISTRY } from "./constants";

const loadingFonts = new Map<string, Promise<void>>();
const loadedFontParts = new Map<
    string,
    { weights: Set<number>; styles: Set<FontStyle>; pairs: Set<string> }
>();
type LoadedFontParts = { weights: Set<number>; styles: Set<FontStyle>; pairs: Set<string> };

const normalizeFamily = (family: string): string => {
    return family.replace(/['"]/g, "").trim();
};

const formatError = (err: unknown): { name: string; message: string } => {
    const name = (err as { name?: string })?.name ?? "UnknownError";
    const message = (err as { message?: string })?.message ?? String(err);

    return { name, message };
};

const buildFontLoadError = (
    family: string,
    failures: Array<{ url: string; err: unknown }>,
): Error => {
    const urls = failures.map((item) => item.url).join(", ");
    const details = failures
        .map((item) => {
            const { name, message } = formatError(item.err);

            return `${item.url} -> ${name}: ${message}`;
        })
        .join("\n");

    return new Error(
        `Ошибка загрузки шрифта\n` +
            `Семейство: ${family}\n` +
            `URL: ${urls}\n` +
            `Детали:\n${details}\n`,
    );
};

const isFontApiAvailable = (): boolean => {
    return typeof document !== "undefined" && typeof FontFace !== "undefined";
};

const refreshTextWidgetsByFontFamily = (family: string): void => {
    const widgets = canv.canvasController.findAllWidgetsByConstructorName("textV2");

    for (const widget of widgets) {
        const data = widget.getData();
        if (!data || data.fontFamily !== family) continue;

        widget.refreshLayoutAfterFontLoad();
    }
};

const loadFontVariant = async (
    family: string,
    file: FontSourceFile,
    targetWeight: number,
    targetStyle: FontStyle,
): Promise<void> => {
    const asset = await getFontAsset(file.url);
    // Используем общий ArrayBuffer из assetStore как оптимизацию памяти.
    // Убрать оптимизацию (раскомментировать), если появятся проблемы совместимости/рендеринга:
    // const font = new FontFace(family, asset.slice(0), { weight: String(targetWeight), style: targetStyle });
    const font = new FontFace(family, asset, {
        weight: String(targetWeight),
        style: targetStyle,
    });
    const loaded = await font.load();
    document.fonts.add(loaded);
};

const applyLoadedFont = (family: string): void => {
    clearTextWidthCache(family);
    refreshTextWidgetsByFontFamily(family);
    canv.redraw();
};

export const ensureFontLoaded = async (
    fontFamily: string,
    requirements: FontLoadRequirements,
): Promise<void> => {
    const normalizedFamily = normalizeFamily(fontFamily);
    const source = FONT_REGISTRY[normalizedFamily];

    if (!source) {
        return Promise.resolve();
    }

    const cached = loadingFonts.get(normalizedFamily);
    if (cached) {
        return cached.then(() => ensureFontLoaded(normalizedFamily, requirements));
    }

    if (isRequirementsSatisfied(normalizedFamily, requirements)) {
        return Promise.resolve();
    }

    const loadPromise = (async (): Promise<void> => {
        try {
            const variantsToLoad = selectFontVariants(
                source,
                requirements,
                loadedFontParts.get(normalizedFamily),
            );
            if (variantsToLoad.length === 0) return;

            const isLoaded = await loadFontFiles(normalizedFamily, variantsToLoad);
            if (isLoaded) {
                markLoadedFontParts(normalizedFamily, variantsToLoad);
                applyLoadedFont(normalizedFamily);
            }
        } finally {
            loadingFonts.delete(normalizedFamily);
        }
    })();

    loadingFonts.set(normalizedFamily, loadPromise);

    return loadPromise;
};

export const isFontRequirementsLoaded = (
    fontFamily: string,
    requirements: FontLoadRequirements,
): boolean => {
    const normalizedFamily = normalizeFamily(fontFamily);
    const source = FONT_REGISTRY[normalizedFamily];
    if (!source) {
        return true;
    }

    return isRequirementsSatisfied(normalizedFamily, requirements);
};

export const ensureFontVariantLoaded = async (
    fontFamily: string,
    variant: "bold" | "italic" | "bold-italic",
): Promise<boolean> => {
    if (!hasFontVariant(fontFamily, variant)) {
        return false;
    }

    const requirements =
        variant === "bold"
            ? {
                  weights: new Set<number>([700]),
                  styles: new Set<"normal">(["normal"]),
              }
            : variant === "italic"
              ? {
                    weights: new Set<number>([400]),
                    styles: new Set<"italic">(["italic"]),
                }
              : {
                    weights: new Set<number>([700]),
                    styles: new Set<"italic">(["italic"]),
                };

    if (isFontRequirementsLoaded(fontFamily, requirements)) {
        return true;
    }

    try {
        retryFailedFontFamilyAssets(fontFamily);
        await ensureFontLoaded(fontFamily, requirements);
        return isFontRequirementsLoaded(fontFamily, requirements);
    } catch {
        return false;
    }
};

export const retryFailedFontFamilyAssets = (fontFamily: string): void => {
    const normalizedFamily = normalizeFamily(fontFamily);
    const source = FONT_REGISTRY[normalizedFamily];
    if (!source) {
        return;
    }

    loadingFonts.delete(normalizedFamily);
    for (const file of source) {
        invalidateFailedFontAsset(file.url);
    }
};

const getLoadedFontParts = (family: string): LoadedFontParts => {
    const existing = loadedFontParts.get(family);
    if (existing) return existing;

    const created = {
        weights: new Set<number>(),
        styles: new Set<FontStyle>(),
        pairs: new Set<string>(),
    };
    loadedFontParts.set(family, created);

    return created;
};

const markLoadedFontParts = (family: string, variants: FontVariantToLoad[]): void => {
    const store = getLoadedFontParts(family);

    for (const variant of variants) {
        store.weights.add(variant.targetWeight);
        store.styles.add(variant.targetStyle);
        store.pairs.add(`${variant.targetWeight}:${variant.targetStyle}`);
    }
};

const isRequirementsSatisfied = (family: string, requirements: FontLoadRequirements): boolean => {
    const store = loadedFontParts.get(family);
    if (!store) return false;

    for (const weight of requirements.weights) {
        for (const style of requirements.styles) {
            if (!store.pairs.has(`${weight}:${style}`)) {
                return false;
            }
        }
    }

    return true;
};

const selectFontVariants = (
    source: FontSourceFile[],
    requirements: FontLoadRequirements,
    loaded?: LoadedFontParts,
): FontVariantToLoad[] => {
    const requestedWeights: number[] = Array.from(requirements.weights);
    const requestedStyles: FontStyle[] = Array.from(requirements.styles);
    const result: FontVariantToLoad[] = [];
    const selectedKeys = new Set<string>();

    for (const targetWeight of requestedWeights) {
        for (const targetStyle of requestedStyles) {
            if (loaded?.pairs.has(`${targetWeight}:${targetStyle}`)) {
                continue;
            }

            const file = resolveClosestFontFile(source, targetWeight, targetStyle);
            if (!file) continue;

            const key = `${targetWeight}:${targetStyle}:${file.url}`;
            if (selectedKeys.has(key)) continue;
            selectedKeys.add(key);

            result.push({ file, targetWeight, targetStyle });
        }
    }

    return result;
};

const loadFontFiles = async (family: string, variants: FontVariantToLoad[]): Promise<boolean> => {
    if (!isFontApiAvailable()) {
        return false;
    }

    const results = await Promise.allSettled(
        variants.map((variant) =>
            loadFontVariant(family, variant.file, variant.targetWeight, variant.targetStyle),
        ),
    );

    const failures: Array<{ url: string; err: unknown }> = [];
    for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (result.status !== "rejected") continue;

        const variant = variants[i];
        if (variant) {
            failures.push({ url: variant.file.url, err: result.reason });
        }
    }

    if (failures.length > 0) {
        canv.captureException(buildFontLoadError(family, failures));

        return false;
    }

    return true;
};

export const resolveClosestFontFile = (
    source: FontSourceFile[],
    targetWeight: number,
    targetStyle: FontStyle,
): FontSourceFile | null => {
    if (source.length === 0) return null;

    const exactStyle = source.filter((file) => file.style === targetStyle);
    const candidates = exactStyle.length > 0 ? exactStyle : source;
    const first = candidates[0];
    if (!first) return null;

    let best = first;
    let bestDiff = Math.abs(first.weight - targetWeight);

    for (const candidate of candidates) {
        const diff = Math.abs(candidate.weight - targetWeight);
        if (diff < bestDiff) {
            best = candidate;
            bestDiff = diff;
        }
    }

    return best;
};

export const getFontRequirementsForTextData = (lines: TextLine[]): FontLoadRequirements => {
    let hasBold = false;
    let hasRegular = false;
    let hasItalic = false;
    let hasNormal = false;
    let hasAnySegments = false;

    for (const line of lines) {
        for (const segment of line.segments) {
            hasAnySegments = true;
            if (segment.bold) {
                hasBold = true;
            } else {
                hasRegular = true;
            }
            if (segment.italic) {
                hasItalic = true;
            } else {
                hasNormal = true;
            }
            if (hasBold && hasRegular && hasItalic && hasNormal) {
                break;
            }
        }
        if (hasBold && hasRegular && hasItalic && hasNormal) {
            break;
        }
    }

    if (!hasAnySegments) {
        hasRegular = true;
        hasNormal = true;
    }

    const weights = new Set<number>();
    const styles = new Set<FontStyle>();

    if (hasRegular) weights.add(400);
    if (hasBold) weights.add(700);
    if (hasNormal) styles.add("normal");
    if (hasItalic) styles.add("italic");

    return { weights, styles };
};
